import { type UseMutationResult } from '@tanstack/react-query';

import { parseOrThrow } from '../../../lib/api/contract.js';
import { useApiMutation } from '../../../lib/api/hooks.js';
import { roomTypeAmenitiesEndpoints, roomTypes } from './endpoints.js';
import { type CreatedRoomType, type RoomTypeFormInput, createdRoomType } from './schemas.js';

/**
 * Writing a room type.
 *
 * Follows the same pattern as every other module: a fresh idempotency key per
 * create, list/detail invalidation, and the response is parsed.
 */

const WRITTEN_KEYS = roomTypes.writtenKeys;

export function createRoomTypePayload(values: RoomTypeFormInput): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    propertyId: values.propertyId,
    code: values.code,
    name: values.name,
    maxOccupancy: Number(values.maxOccupancy),
  };

  if (values.displayName !== '') payload.displayName = values.displayName;
  if (values.description !== '') payload.description = values.description;
  if (values.sizeInSqm !== '') payload.sizeInSqm = values.sizeInSqm;
  if (values.bedType !== '') payload.bedType = values.bedType;
  if (values.totalNoOfRooms !== '') payload.totalNoOfRooms = Number(values.totalNoOfRooms);
  if (values.pmsRoomTypeId !== '') payload.pmsRoomTypeId = values.pmsRoomTypeId;
  if (values.status !== '') payload.status = values.status;

  const amenities = Object.entries(values.selectedAmenities)
    .filter(([, checked]) => checked)
    .map(([amenityId]) => ({ amenityId, isFree: values.amenityFree[amenityId] ?? true }));
  payload.amenities = amenities;

  return payload;
}

export function updateRoomTypePayload(
  values: RoomTypeFormInput,
  version: number,
): Record<string, unknown> {
  const payload: Record<string, unknown> = {
    version,
    name: values.name,
    maxOccupancy: Number(values.maxOccupancy),
    displayName: values.displayName === '' ? null : values.displayName,
    description: values.description === '' ? null : values.description,
    sizeInSqm: values.sizeInSqm === '' ? null : values.sizeInSqm,
    bedType: values.bedType === '' ? null : values.bedType,
    pmsRoomTypeId: values.pmsRoomTypeId === '' ? null : values.pmsRoomTypeId,
    status: values.status,
  };

  if (values.totalNoOfRooms !== '') payload.totalNoOfRooms = Number(values.totalNoOfRooms);

  const amenities = Object.entries(values.selectedAmenities)
    .filter(([, checked]) => checked)
    .map(([amenityId]) => ({ amenityId, isFree: values.amenityFree[amenityId] ?? true }));
  payload.amenities = amenities;

  return payload;
}

export function useCreateRoomType(): UseMutationResult<
  CreatedRoomType,
  unknown,
  RoomTypeFormInput
> {
  return useApiMutation<RoomTypeFormInput, CreatedRoomType>({
    mutationFn: async (api, input) => {
      const payload = await api.post<unknown>(
        roomTypes.endpoints.create,
        createRoomTypePayload(input),
        crypto.randomUUID(),
      );
      return parseOrThrow(createdRoomType, payload, roomTypes.endpoints.create);
    },
    invalidate: WRITTEN_KEYS,
  });
}

export interface UpdateRoomTypeVariables {
  readonly roomTypeId: string;
  readonly version: number;
  readonly values: RoomTypeFormInput;
}

export function useUpdateRoomType(): UseMutationResult<unknown, unknown, UpdateRoomTypeVariables> {
  return useApiMutation<UpdateRoomTypeVariables, unknown>({
    mutationFn: async (api, { roomTypeId, version, values }) => {
      await api.patch<unknown>(
        roomTypes.endpoints.update(roomTypeId),
        updateRoomTypePayload(values, version),
      );
    },
    invalidate: WRITTEN_KEYS,
  });
}

export interface UpdateRoomTypeAmenitiesVariables {
  readonly roomTypeId: string;
  readonly amenities: { readonly amenityId: string; readonly isFree: boolean }[];
}

export function useUpdateRoomTypeAmenities(): UseMutationResult<
  unknown,
  unknown,
  UpdateRoomTypeAmenitiesVariables
> {
  return useApiMutation<UpdateRoomTypeAmenitiesVariables, unknown>({
    mutationFn: async (api, { roomTypeId, amenities }) => {
      await api.put(roomTypeAmenitiesEndpoints.update(roomTypeId), { amenities });
    },
    invalidate: [['room-type-amenities']],
  });
}

export interface DeleteRoomTypeVariables {
  readonly roomTypeId: string;
  readonly version: number;
}

/**
 * Deleting a room type.
 *
 * Soft, always: the service sets `deleted_at` and leaves the row so rooms and
 * reservations that reference it remain valid.
 */
export function useSoftDeleteRoomType(): UseMutationResult<
  unknown,
  unknown,
  DeleteRoomTypeVariables
> {
  return useApiMutation<DeleteRoomTypeVariables, unknown>({
    mutationFn: async (api, { roomTypeId, version }) => {
      await api.delete<unknown>(
        `${roomTypes.endpoints.delete(roomTypeId)}?version=${encodeURIComponent(String(version))}`,
      );
    },
    invalidate: WRITTEN_KEYS,
  });
}
